/**
 * Revenue by Channel Chart Component
 * Based on: documentation/cursor/owner-dashboard-ui-specification-v1-final.md
 * Section 4, Component 7: Revenue by Channel Chart
 */

import { useState, useEffect, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import ChartTooltipPortal from './ChartTooltipPortal.jsx';
import ChartAnnotations from './ChartAnnotations.jsx';
import ChartInfoIcon from './analytics/ChartInfoIcon.jsx';
import { transformRevenueData } from '../../utils/analyticsTransform.js';
import { getChannelColorVar } from '../../utils/channelColorUtils.js';
import '../../styles/chart-card.css';
import '../../styles/charts.css';

export default function RevenueChart({ data, channels, onSegmentClick, selectedChannels = [], annotations = [], periodRevenue, isMobile = false }) {
  const { theme } = useTheme();
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  // ResizeObserver-based sizing to eliminate Recharts warnings
  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          setContainerSize({ width, height });
        }
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  if (!data || data.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <h3 style={styles.title}>Revenue by Channel</h3>
          <p style={styles.subtitle}>Revenue breakdown by booking source</p>
        </div>
        <div style={styles.emptyState}>
          No revenue data available
        </div>
      </div>
    );
  }

  // Backend already filters by channel and date range (Reporting Period is authoritative)
  // Transform row-form data to chart-ready pivoted format
  const chartData = transformRevenueData(data, channels || []);

  // Get channel names and colors (use shared strict resolver - single source of truth)
  // Do NOT use backend color_hex to prevent drift
  const allChannelNames = (channels || []).map(c => c.name);
  const channelColors = {};
  (channels || []).forEach(channel => {
    const channelKey = channel.master_channel_key || channel.code;
    channelColors[channel.name] = channelKey ? getChannelColorVar(channelKey) : 'var(--color-text-tertiary, #737373)';
  });

  // Only show channels with data (non-zero in current view). Legend and tooltip reflect active series only.
  const activeChannels = allChannelNames.filter((name) =>
    chartData.some((d) => (d[name] || 0) > 0)
  );

  // Tiny _zero so Recharts draws a segment (0-height can be skipped); LabelList positions at stack top.
  const chartDataWithMeta = chartData.map((d) => ({ ...d, _zero: 1e-9 }));
  const formatTotalLabel = (v) => {
    if (v == null || Number(v) <= 0) return '';
    return `$${Math.round(Number(v)).toLocaleString('en-US')}`;
  };

  // Theme-aware colors
  const gridColor = theme === 'dark' ? 'var(--color-border-light)' : '#e2e8f0';
  const textColor = theme === 'dark' ? 'var(--color-text-secondary)' : '#64748b';
  const tooltipBg = theme === 'dark' ? 'var(--color-bg-primary)' : '#fff';
  const tooltipBorder = theme === 'dark' ? 'var(--color-border-medium)' : '#e2e8f0';

  const handleBarClick = (data) => {
    if (onSegmentClick && data) {
      const monthDate = data.monthDate || new Date();
      onSegmentClick({
        type: 'revenue',
        month: monthDate,
        monthLabel: data.month,
        data: data,
      });
    }
  };

  return (
    <div className="chart-card" style={styles.card}>
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h3 style={styles.title}>
            Revenue by Channel
            {periodRevenue !== undefined && (
              <span style={{ fontSize: '14px', fontWeight: '400', color: 'var(--color-text-secondary, #404040)', marginLeft: '8px' }}>
                (Period Revenue: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(periodRevenue)})
              </span>
            )}
          </h3>
          <ChartInfoIcon
            title="Revenue by Channel"
            definition="Revenue earned during the selected period, broken out by channel (e.g. Airbnb, VRBO). Each bar is a month; segments are channels."
            limitations="Revenue is attributed to the month of check-in. Use it to see which channels drive income and how that changes over time. Excludes cancelled stays."
            futureMonthsNote
          />
        </div>
        <p style={styles.subtitle}>Revenue breakdown by booking source</p>
      </div>
      <div ref={containerRef} style={styles.chartContainer}>
        {annotations && annotations.length > 0 && (
          <ChartAnnotations annotations={annotations} chartData={chartData} xKey="month" />
        )}
        {containerSize.width > 0 && containerSize.height > 0 && (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={chartDataWithMeta} 
            margin={{ top: 50, right: 30, left: 20, bottom: 5 }}
            style={{ cursor: onSegmentClick ? 'pointer' : 'default' }}
          >
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke={gridColor}
              strokeWidth={0.5} /* Recommendation 16: stroke-width 0.5px */
            />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12, fill: textColor, fontWeight: 500 }} /* Recommendation 16: weight 500 */
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              tick={{ fontSize: 12, fill: textColor, fontWeight: 500 }} /* Recommendation 16: weight 500 */
              tickFormatter={(value) => `$${value.toLocaleString()}`}
            />
            <Tooltip
              content={(props) => (
                <ChartTooltipPortal {...props} chartType="revenue" containerRef={containerRef} />
              )}
              cursor={{ fill: 'transparent' }}
              wrapperStyle={{ pointerEvents: 'none' }}
            />
            <Legend
              wrapperStyle={{
                paddingTop: '20px',
                paddingBottom: isMobile ? '12px' : undefined,
                color: textColor,
              }}
              iconType="square"
              formatter={(value) => <span style={{ color: textColor }}>{value}</span>}
            />
            {activeChannels.map((channelName) => (
              <Bar
                key={channelName}
                dataKey={channelName}
                stackId="revenue"
                fill={channelColors[channelName] || '#6B7280'}
                name={channelName}
                onClick={(data) => {
                  if (data && data.activePayload && data.activePayload[0]) {
                    handleBarClick(data.activePayload[0].payload);
                  }
                }}
              />
            ))}
            <Bar
              dataKey="_zero"
              stackId="revenue"
              fill="transparent"
              isAnimationActive={false}
              legendType="none"
            >
              <LabelList
                dataKey="total_value"
                position="top"
                formatter={formatTotalLabel}
                style={{ fill: 'var(--color-text-primary, #111827)', fontSize: 12, fontWeight: 700 }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

const styles = {
  card: {
    backgroundColor: 'var(--color-bg-primary)',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-lg)',
      padding: 'var(--spacing-3)', /* Recommendation 8: 24px (was var(--spacing-5) = 40px) */
      boxShadow: 'var(--shadow-md)',
      transition: 'var(--transition-base)',
  },
  header: {
    marginBottom: 'var(--spacing-5)',
  },
  title: {
    fontSize: 'var(--font-size-lg)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    margin: 0,
    marginBottom: 'var(--spacing-1)',
  },
  subtitle: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    margin: 0,
  },
  chartContainer: {
    height: '300px',
    minHeight: '300px',
    width: '100%',
    minWidth: '200px',
    position: 'relative',
    overflow: 'visible',
  },
  emptyState: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-text-tertiary)',
    fontSize: 'var(--font-size-sm)',
    fontStyle: 'italic',
  },
};

